import { useI18n } from "i18n";
import {
  MAP_NAVIGATION_TOOLTIP_DURATION_MS,
  MAP_NAVIGATION_TOOLTIP_SHOW_DELAY_MS,
  MAP_NAVIGATION_USER_AVATAR_ANIMATION_MS,
} from "modules/event";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { Lottie } from "@remo-co/ui-core/src/components/Lottie";
import { selectTargetMapNavigation } from "modules/mapNavigation/redux/selectors";
import React from "react";
import { useSelector } from "react-redux";
import { IUser } from "modules/app/types";
import { getUserName } from "modules/userProfile";
import avatarRadarData from "../../assets/avatar-radar.json";
import useStyles from "./styles";

interface IUserLocatingAnimationProps {
  currentUser: IUser | undefined;
  userInSpace: IUser;
  userId: string;
  onAnimationStart: () => void;
  onAnimationEnd: () => void;
}

const UserLocatingAnimation = (props: IUserLocatingAnimationProps) => {
  const { currentUser, userId, userInSpace, onAnimationStart, onAnimationEnd } =
    props;
  const [tooltipText, setTooltipText] = React.useState("");
  const [isUserLocated, setIsUserLocated] = React.useState(false);
  const [openTooltip, setOpenTooltip] = React.useState(false);
  const [isLottieAnimationStopped, setIsLottieAnimationStopped] =
    React.useState(false);
  const { t } = useI18n(["userLocator"]);
  const target = useSelector(selectTargetMapNavigation);
  const userLocatedTimerRef = React.useRef<number>();
  const animationTimerRef = React.useRef<number>();
  const tooltipTimerRef = React.useRef<number>();
  const classes = useStyles();
  const isItMe = currentUser?.id === userId;

  const handleTooltipOpen = () => {
    setOpenTooltip(true);
    onAnimationStart?.();
  };

  const handleTooltipClose = () => {
    setOpenTooltip(false);
    onAnimationEnd?.();
  };

  // Focus on this User? Keep focus on for a duration (MAP_NAVIGATION_TOOLTIP_DURATION_MS)
  React.useEffect(() => {
    if (
      target?.highlight?.type === "user" &&
      target?.highlight?.id === userId
    ) {
      // userLocation
      if (userLocatedTimerRef.current) {
        window.clearTimeout(userLocatedTimerRef.current);
      }

      // Close tooltip if it's already open to refresh it's position
      handleTooltipClose();

      // Show tooltip - after 10 ms
      userLocatedTimerRef.current = window.setTimeout(() => {
        handleTooltipOpen();
        setIsUserLocated(true);
      }, MAP_NAVIGATION_TOOLTIP_SHOW_DELAY_MS);

      // Hide Tooltip - after 4000 ms
      userLocatedTimerRef.current = window.setTimeout(() => {
        handleTooltipClose();
        setIsUserLocated(false);
      }, MAP_NAVIGATION_TOOLTIP_DURATION_MS);

      // Start Animation - instantly
      setIsLottieAnimationStopped(false);

      if (animationTimerRef.current) {
        window.clearTimeout(animationTimerRef.current);
      }
      // Stop Animation - after 1750 ms or the custom override, so we can do another animation if needed
      animationTimerRef.current = window.setTimeout(() => {
        setIsLottieAnimationStopped(true);
      }, target?.customAnimationDurationOverride || MAP_NAVIGATION_USER_AVATAR_ANIMATION_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, userInSpace]);

  React.useEffect(() => {
    // Tooltip for User being located - Self or other user
    setTooltipText(
      isItMe
        ? t("userLocator:you.are.here")
        : t("userLocator:user.is.here", {
            name: getUserName(userInSpace),
          }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInSpace]);

  // clean up timers when unmounting
  React.useEffect(
    () => () => {
      if (userLocatedTimerRef.current)
        clearTimeout(userLocatedTimerRef.current);

      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);

      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    },
    [],
  );

  if (!isUserLocated) return null;

  return (
    <Tooltip
      placement="bottom"
      title={tooltipText}
      open={openTooltip}
      arrow
      color="var(--DrankGrey)"
      data-testid="user-locating-animation"
      PopperProps={{
        popperOptions: {
          modifiers: [
            {
              name: "offset",
              enabled: true,
              options: {
                offset: [0, -35],
              },
            },
          ],
        },
      }}
    >
      <div className={classes.avatarRadar}>
        <Lottie
          animationData={avatarRadarData}
          isPaused={isLottieAnimationStopped}
          loop={false}
        />
      </div>
    </Tooltip>
  );
};

export default React.memo(UserLocatingAnimation);
